/**
 * `<SlotCardFreeText>` — textarea for the `free-text` slot type.
 *
 * Voice branch (per TDD §6.2 + §4.1): when `slot.voiceCapability` is
 * set AND the (rare) reconstruction path supplies an `audioPath`, the
 * card renders the transcript prominently and a small "Recorded
 * audio" affordance below pointing at the audio file. Voice-recording
 * UI itself (capture, level meter, stop button) is Phase 5 polish;
 * this PR ships the read/render path so a resumed session that had
 * voice answers shows them correctly.
 *
 * `rendererHint`:
 *   - 'short' → 1 row, single-line submit-on-Enter feel
 *   - 'long'  → 4 rows, classic multi-line textarea
 *   - undefined → 2 rows (default, matches BulkInterviewCard)
 */

import type {
  SlotDefinition,
  FreeTextWithVoice,
} from '@volley/crucible/interview/types.js';

export interface SlotCardFreeTextProps {
  slot: SlotDefinition;
  /** Plain transcript text. The runtime `FreeTextWithVoice` shape is
   *  reconstructed by the parent from this value + `audioPath`. */
  value: string;
  onChange: (next: string) => void;
  /** Optional audio path — only meaningful when the slot has a
   *  voiceCapability. Read off `state.slotMeta[slot.id].audioPath`
   *  per TDD §6.2. Undefined means voice was never recorded. */
  audioPath?: string;
  prefilledUntouched: boolean;
}

/** Pure helper — derive the runtime `FreeTextWithVoice | string` shape
 *  from the persisted transcript + audio path. Exported for tests so
 *  the persistence-mapping spec is pinned. */
export function reconstructFreeTextValue(
  transcript: string,
  audioPath?: string,
  hasVoiceCapability?: boolean,
): string | FreeTextWithVoice {
  if (hasVoiceCapability && audioPath !== undefined && audioPath.length > 0) {
    return { kind: 'voice', transcript, audioPath };
  }
  return transcript;
}

function rowsForHint(hint: SlotDefinition['rendererHint']): number {
  if (hint === 'short') return 1;
  if (hint === 'long') return 4;
  return 2;
}

export function SlotCardFreeText({
  slot,
  value,
  onChange,
  audioPath,
  prefilledUntouched,
}: SlotCardFreeTextProps): React.JSX.Element {
  const rows = rowsForHint(slot.rendererHint);
  const hasVoice = slot.voiceCapability !== undefined;
  const showAudioAffordance = hasVoice && audioPath !== undefined && audioPath.length > 0;

  return (
    <div data-testid={`slot-card-free-text-${slot.id}`} data-has-voice={hasVoice ? 'true' : 'false'}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={slot.example ?? 'Type your answer…'}
        data-testid={`slot-card-free-text-${slot.id}-textarea`}
        data-renderer-hint={slot.rendererHint ?? 'default'}
        className={`w-full resize-none rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-sm leading-relaxed placeholder:text-white/30 focus:border-crucible-accent focus:outline-none ${
          prefilledUntouched ? 'italic text-white/50' : 'text-white/90'
        }`}
      />

      {hasVoice && (
        <p className="mt-1.5 text-[10px] uppercase tracking-wide text-white/30">
          Voice optional — you can also speak your answer (recorder coming soon).
        </p>
      )}

      {showAudioAffordance && (
        <div
          className="mt-2 flex items-center gap-2 rounded-md border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white/70"
          data-testid={`slot-card-free-text-${slot.id}-audio`}
          data-audio-path={audioPath}
        >
          <span aria-hidden="true">🎙</span>
          <span className="flex-1 truncate">Recorded audio: {audioPath}</span>
        </div>
      )}
    </div>
  );
}
